import { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Fuse from 'fuse.js';
import { collection, onSnapshot, query, orderBy, doc, limit, where, getDocs } from 'firebase/firestore';
import { db, handleFirestoreError, OperationType } from '../../firebase';
import { Product, StoreConfig, CartItem } from '../../types';
import { CATEGORIES } from '../../constants';
import Navbar from './Navbar';
import ProductCard from './ProductCard';
import ProductCardSkeleton from './ProductCardSkeleton';
import ProductFeedLayouts from './ProductFeedLayouts';
import QuantitySelector from './QuantitySelector';
import ProductLightbox from './ProductLightbox';

const OrderModal = lazy(() => import('./OrderModal'));
const CustomerOrdersModal = lazy(() => import('./CustomerOrdersModal'));
import { formatPrice, formatQuantityUnit, cleanCategoryName } from '../../lib/utils';
import { motion, AnimatePresence } from 'motion/react';
import { Star, X, Grid, ShoppingBag, ShoppingBasket, Heart, Home as HomeIcon, CupSoda, Sparkles, Pencil, CheckCircle2, ChevronLeft, ChevronRight, Plus, Minus, ZoomIn, Share2, Search, Smile, Flame, Zap, Tag, AlertTriangle, RotateCw, PackageX } from 'lucide-react';

const getCategoryIcon = (category: string | null, isSelected: boolean, sizeClass = "w-5 h-5 md:w-4 h-4") => {
  const baseIconClass = `${sizeClass} transition-transform duration-300 group-hover:scale-110`;
  
  if (!category) {
    return (
      <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
        isSelected 
          ? 'bg-white/20 text-white' 
          : 'bg-indigo-50 text-indigo-600 group-hover:bg-indigo-100'
      }`}>
        <Grid className={baseIconClass} />
      </div>
    );
  }

  const catLower = category.toLowerCase();

  if (catLower.includes("daily") || catLower.includes("rozana")) {
    return (
      <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
        isSelected 
          ? 'bg-white/20 text-white' 
          : 'bg-amber-50 text-amber-600 group-hover:bg-amber-100'
      }`}>
        <ShoppingBag className={baseIconClass} />
      </div>
    );
  }
  
  if (catLower.includes("grocer") || catLower.includes("rashan")) {
    return (
      <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
        isSelected 
          ? 'bg-white/20 text-white' 
          : 'bg-emerald-50 text-emerald-600 group-hover:bg-emerald-100'
      }`}>
        <ShoppingBasket className={baseIconClass} />
      </div>
    );
  }
  
  if (catLower.includes("personal") || catLower.includes("dekhbhal")) {
    return (
      <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
        isSelected 
          ? 'bg-white/20 text-white' 
          : 'bg-teal-50 text-teal-600 group-hover:bg-teal-100'
      }`}>
        <Smile className={baseIconClass} />
      </div>
    );
  }
  
  if (catLower.includes("home") || catLower.includes("ghar")) {
    return (
      <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
        isSelected 
          ? 'bg-white/20 text-white' 
          : 'bg-blue-50 text-blue-600 group-hover:bg-blue-100'
      }`}>
        <HomeIcon className={baseIconClass} />
      </div>
    );
  }
  
  if (catLower.includes("beverage") || catLower.includes("peene")) {
    return (
      <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
        isSelected 
          ? 'bg-white/20 text-white' 
          : 'bg-orange-50 text-orange-600 group-hover:bg-orange-100'
      }`}>
        <CupSoda className={baseIconClass} />
      </div>
    );
  }
  
  if (catLower.includes("cosmetic") || catLower.includes("shringar")) {
    return (
      <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
        isSelected 
          ? 'bg-white/20 text-white' 
          : 'bg-fuchsia-50 text-fuchsia-600 group-hover:bg-fuchsia-100'
      }`}>
        <Sparkles className={baseIconClass} />
      </div>
    );
  }
  
  if (catLower.includes("stationery") || catLower.includes("lekhan")) {
    return (
      <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
        isSelected 
          ? 'bg-white/20 text-white' 
          : 'bg-violet-50 text-violet-600 group-hover:bg-violet-100'
      }`}>
        <Pencil className={baseIconClass} />
      </div>
    );
  }
  
  return (
    <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
      isSelected 
        ? 'bg-white/20 text-white' 
        : 'bg-gray-50 text-gray-600 group-hover:bg-gray-100'
    }`}>
      <ShoppingBag className={baseIconClass} />
    </div>
  );
};

const getTextSizeClasses = (size: string | undefined, isTextBanner: boolean = false) => {
  if (isTextBanner) {
    switch (size) {
      case 'xs': return 'text-[9px] sm:text-[10px] md:text-xs';
      case 'sm': return 'text-[10px] sm:text-xs md:text-sm';
      case 'md': return 'text-xs sm:text-sm md:text-base';
      case 'lg': return 'text-sm sm:text-base md:text-lg';
      case 'xl': return 'text-base sm:text-lg md:text-xl';
      case '2xl': return 'text-lg sm:text-xl md:text-2xl';
      case '3xl': return 'text-xl sm:text-2xl md:text-3xl';
      default: return 'text-xs sm:text-sm';
    }
  } else {
    switch (size) {
      case 'xs': return 'text-[8px] sm:text-[10px] md:text-xs';
      case 'sm': return 'text-[10px] sm:text-xs md:text-sm';
      case 'md': return 'text-xs sm:text-sm md:text-base';
      case 'lg': return 'text-sm sm:text-base md:text-lg';
      case 'xl': return 'text-base sm:text-lg md:text-xl';
      case '2xl': return 'text-lg sm:text-xl md:text-2xl';
      case '3xl': return 'text-xl sm:text-2xl md:text-3xl';
      default: return 'text-[10px] sm:text-xs md:text-sm';
    }
  }
};

const StoreBanner = ({
  banner,
  isBanner2 = false,
  activeIdx = 0,
  setActiveIdx,
}: {
  banner: any;
  isBanner2?: boolean;
  activeIdx: number;
  setActiveIdx?: (idx: number) => void;
}) => {
  const [aspectRatio, setAspectRatio] = useState<number | null>(3);

  const urls = banner?.urls && banner.urls.length > 0 
    ? banner.urls.filter(Boolean) 
    : (banner?.url ? [banner.url] : []);

  const selectedIdx = banner?.selectedUrlIdx ?? 0;
  const selectedUrl = urls[selectedIdx] || urls[0] || "";

  useEffect(() => {
    if (!selectedUrl || (banner?.type !== 'image' && banner?.type !== 'video')) {
      setAspectRatio(null);
      return;
    }

    if (banner.type === 'image') {
      const img = new Image();
      img.src = selectedUrl;
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) {
          setAspectRatio(img.naturalWidth / img.naturalHeight);
        }
      };
      img.onerror = () => {
        setAspectRatio(null);
      };
    } else if (banner.type === 'video') {
      const video = document.createElement('video');
      video.src = selectedUrl;
      video.autoplay = false;
      video.muted = true;
      video.onloadedmetadata = () => {
        if (video.videoWidth && video.videoHeight) {
          setAspectRatio(video.videoWidth / video.videoHeight);
        }
      };
      video.onerror = () => {
        setAspectRatio(null);
      };
    }
  }, [selectedUrl, banner?.type]);

  if (!banner || banner.type === 'none') return null;
  if (urls.length === 0 && banner.type !== 'text') return null;

  const isMarqueeEnabled = banner.style 
    ? (banner.style === 'marquee') 
    : (isBanner2 ? (banner.enableMarquee === true) : (banner.enableMarquee !== false));

  let selectedStyle = banner.style || (isMarqueeEnabled ? 'marquee' : 'carousel');
  if (selectedStyle === 'spotlight') {
    selectedStyle = 'carousel';
  }

  const isLTR = banner.marqueeDirection === 'ltr';
  const animationClass = isLTR ? 'animate-marquee-ltr' : 'animate-marquee-rtl';

  if (banner.type === 'text') {
    const textValue = banner.text || 'Special Offer';
    const repeats = Array(8).fill(textValue);
    return (
      <section 
        className="w-full overflow-hidden select-none relative z-20 flex items-center" 
        style={{ backgroundColor: banner.bgColor || '#0047AB', color: banner.textColor || '#ffffff' }}
      >
        <div className="py-2.5 w-full overflow-hidden relative">
          {selectedStyle !== 'marquee' ? (
            <div className={`w-full text-center font-bold uppercase tracking-widest px-4 py-1 ${getTextSizeClasses(banner.textSize, true)}`}>
              <span>✨ {textValue} ✨</span>
            </div>
          ) : (
            <div 
              key={`marquee-text-${banner.marqueeSpeed}-${isLTR}`}
              className={`${animationClass} hover:[animation-play-state:paused] flex whitespace-nowrap gap-16 font-bold uppercase tracking-widest shrink-0 ${getTextSizeClasses(banner.textSize, true)}`}
              style={{ 
                animation: `${isLTR ? "marquee-ltr" : "marquee-rtl"} ${banner.marqueeSpeed || 25}s linear infinite`,
                animationDuration: banner.marqueeSpeed ? `${banner.marqueeSpeed}s` : "25s",
                "--marquee-duration": banner.marqueeSpeed ? `${banner.marqueeSpeed}s` : "25s"
              } as React.CSSProperties}
            >
              <div className="flex gap-16 shrink-0">
                {repeats.map((t, idx) => (
                  <span key={`set1-${idx}`} className="flex items-center gap-2">
                    <span>✨</span>
                    <span>{t}</span>
                  </span>
                ))}
              </div>
              <div className="flex gap-16 shrink-0" aria-hidden="true">
                {repeats.map((t, idx) => (
                  <span key={`set2-${idx}`} className="flex items-center gap-2">
                    <span>✨</span>
                    <span>{t}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }

  if (banner.type === 'image' || banner.type === 'video') {
    let marqueeUrls = [...urls];
    if (marqueeUrls.length > 0 && selectedStyle === 'marquee') {
      while (marqueeUrls.length < 15) {
        marqueeUrls = [...marqueeUrls, ...urls];
      }
    }

    // Dynamic style with exact aspect ratio
    const containerStyle: React.CSSProperties = aspectRatio 
      ? { aspectRatio: `${aspectRatio}` } 
      : {};

    const parentClass = aspectRatio
      ? "w-full relative overflow-hidden flex items-center bg-black leading-[0] transition-all duration-300"
      : "w-full relative overflow-hidden flex items-center bg-black leading-[0] min-h-[130px] sm:min-h-[180px] md:min-h-[240px] transition-all duration-300";

    return (
      <section className="w-full overflow-hidden bg-black relative z-20">
        <div style={containerStyle} className={parentClass}>
          {selectedStyle === 'carousel' ? (
            /* Elegant Standard Banner Carousel with next/prev buttons and indicator dots */
            <div className="w-full h-full overflow-hidden flex items-center justify-center group/carousel shadow-inner relative">
              {urls[activeIdx] ? (
                banner.type === 'image' ? (
                  <img 
                    src={urls[activeIdx]} 
                    alt={`Carousel Banner-${activeIdx}`} 
                    width={1200}
                    height={400}
                    className="w-full h-full object-fill bg-black select-none transition-all duration-500" 
                    referrerPolicy="no-referrer" 
                    fetchPriority="high"
                    decoding="async"
                  />
                ) : (
                  <video 
                    src={urls[activeIdx]} 
                    className="w-full h-full object-fill bg-black" 
                    autoPlay 
                    loop 
                    playsInline 
                    muted 
                  />
                )
              ) : null}

              {/* Prev Button Overlay */}
              {urls.length > 1 && setActiveIdx && (
                <button
                  type="button"
                  onClick={() => setActiveIdx((activeIdx - 1 + urls.length) % urls.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/60 hover:bg-black/85 border border-white/10 flex items-center justify-center text-white transition-all opacity-0 group-hover/carousel:opacity-100 duration-300 shadow-md hover:scale-105"
                >
                  <ChevronLeft className="w-5 h-5 stroke-[2.5]" />
                </button>
              )}

              {/* Next Button Overlay */}
              {urls.length > 1 && setActiveIdx && (
                <button
                  type="button"
                  onClick={() => setActiveIdx((activeIdx + 1) % urls.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-black/60 hover:bg-black/85 border border-white/10 flex items-center justify-center text-white transition-all opacity-0 group-hover/carousel:opacity-100 duration-300 shadow-md hover:scale-105"
                >
                  <ChevronRight className="w-5 h-5 stroke-[2.5]" />
                </button>
              )}

              {/* Indicator dots at top/bottom corner */}
              {urls.length > 1 && setActiveIdx && (
                <div 
                  className="absolute bottom-3 right-3 z-30 bg-black/50 backdrop-blur-md px-2.5 py-1.5 rounded-full flex gap-1.5 border border-white/10 shadow-sm leading-none"
                >
                  {urls.map((_: any, idx: number) => {
                    const isSelected = idx === activeIdx;
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setActiveIdx(idx)}
                        className={`h-1.5 rounded-full cursor-pointer transition-all duration-300 ${
                          isSelected 
                            ? 'w-4 bg-brand-accent' 
                            : 'w-1.5 bg-white/40 hover:bg-white/60'
                        }`}
                        title={`Slide ${idx + 1}`}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          ) : selectedStyle === 'grid' ? (
            /* Static Single selected image/video: show only the selected ticked image in full-width, not all */
            <div className="w-full h-full bg-black flex items-center justify-center relative">
              {urls[activeIdx] ? (
                banner.type === 'image' ? (
                  <img 
                    src={urls[activeIdx]} 
                    alt={`Static Selected Banner-${activeIdx}`} 
                    width={1200}
                    height={400}
                    className="w-full h-full object-fill bg-black select-none" 
                    referrerPolicy="no-referrer" 
                    fetchPriority="high"
                    decoding="async"
                  />
                ) : (
                  <video 
                    src={urls[activeIdx]} 
                    className="w-full h-full object-fill bg-black" 
                    autoPlay 
                    loop 
                    playsInline 
                    muted 
                  />
                )
              ) : null}
            </div>
          ) : (
            /* Seamless continuous marquee of images/videos with 3px black gap/line */
            <div 
              key={`marquee-media-${banner.marqueeSpeed}-${isLTR}`}
              className={`${animationClass} hover:[animation-play-state:paused] flex shrink-0 h-full bg-black`}
              style={{ 
                animation: `${isLTR ? "marquee-ltr" : "marquee-rtl"} ${banner.marqueeSpeed || 25}s linear infinite`,
                animationDuration: banner.marqueeSpeed ? `${banner.marqueeSpeed}s` : "25s",
                "--marquee-duration": banner.marqueeSpeed ? `${banner.marqueeSpeed}s` : "25s",
                height: '100%'
              } as React.CSSProperties}
            >
              {/* Set 1 */}
              <div className="flex gap-[3px] shrink-0 h-full bg-black relative">
                {marqueeUrls.map((url, idx) => (
                  <div 
                    key={`set1-${idx}`} 
                    style={containerStyle}
                    className="h-full shrink-0 bg-black relative flex items-center justify-center"
                  >
                    {banner.type === 'image' ? (
                      <img src={url} alt={`Banner Set1-${idx}`} width={1200} height={400} loading="lazy" className="w-full h-full object-fill bg-black select-none" referrerPolicy="no-referrer" decoding="async" />
                    ) : (
                      <video src={url} className="w-full h-full object-fill bg-black" autoPlay loop playsInline muted />
                    )}
                  </div>
                ))}
              </div>
              {/* Set 2 */}
              <div className="flex gap-[3px] shrink-0 h-full bg-black relative" aria-hidden="true">
                {marqueeUrls.map((url, idx) => (
                  <div 
                    key={`set2-${idx}`} 
                    style={containerStyle}
                    className="h-full shrink-0 bg-black relative flex items-center justify-center"
                  >
                    {banner.type === 'image' ? (
                      <img src={url} alt={`Banner Set2-${idx}`} width={1200} height={400} loading="lazy" className="w-full h-full object-fill bg-black select-none" referrerPolicy="no-referrer" decoding="async" />
                    ) : (
                      <video src={url} className="w-full h-full object-fill bg-black" autoPlay loop playsInline muted />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Content text overlay */}
          {banner.text && (
            <div className="absolute inset-x-0 bottom-0 flex justify-center p-3 select-none z-30 pointer-events-none bg-gradient-to-t from-black/40 to-transparent">
              <h3 
                className={`font-bold uppercase tracking-widest leading-snug text-center font-display drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] max-w-[95%] ${getTextSizeClasses(banner.textSize, false)}`}
                style={{ color: banner.textColor || '#ffffff' }}
              >
                {banner.text}
              </h3>
            </div>
          )}
        </div>
      </section>
    );
  }

  return null;
};

const renderBanner = (
  banner: any, 
  isBanner2: boolean = false, 
  activeIdx: number = 0, 
  setActiveIdx?: (idx: number) => void
) => {
  if (!banner || banner.type === 'none') return null;
  
  const urls = banner?.urls && banner.urls.length > 0 
    ? banner.urls.filter(Boolean) 
    : (banner?.url ? [banner.url] : []);
  if (urls.length === 0 && banner.type !== 'text') return null;

  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-10 mt-3 md:mt-4 mb-2">
      <div className="rounded-2xl overflow-hidden shadow-md border border-brand-border/40 bg-black">
        <StoreBanner 
          banner={banner} 
          isBanner2={isBanner2} 
          activeIdx={activeIdx} 
          setActiveIdx={setActiveIdx} 
        />
      </div>
    </div>
  );
};

interface HomeProps {
  config: StoreConfig;
}

export default function Home({ config }: HomeProps) {
  const [masterProductsList, setMasterProductsList] = useState<Product[]>(() => {
    try {
      const saved = localStorage.getItem('rk_products_cache');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [products, setProducts] = useState<Product[]>(masterProductsList);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedCollection, setSelectedCollection] = useState<'bestsellers' | 'trending' | 'newarrivals' | 'offers' | null>(null);
  const [visibleItems, setVisibleItems] = useState(16);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [tempQuantity, setTempQuantity] = useState<number>(1);
  const [isInitialLoad, setIsInitialLoad] = useState(() => {
    try {
      const saved = localStorage.getItem('rk_products_cache');
      const cachedList = saved ? JSON.parse(saved) : [];
      return cachedList.length === 0;
    } catch {
      return true;
    }
  });
  const [productsError, setProductsError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const handleRetry = useCallback(() => {
    setProductsError(null);
    setIsInitialLoad(true);
    setRetryCount((prev) => prev + 1);
  }, []);

  const [activeBanner1Idx, setActiveBanner1Idx] = useState(0);
  const [activeBanner2Idx, setActiveBanner2Idx] = useState(0);
  const showIcons = true;

  const [isCategoryBarVisible, setIsCategoryBarVisible] = useState(true);
  const lastScrollY = useRef(0);

  // Dynamic Scroll Behavior for Category Bar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // If we are near the very top of the page, always show the category bar
      if (currentScrollY < 80) {
        setIsCategoryBarVisible(true);
        lastScrollY.current = currentScrollY;
        return;
      }

      // Check if scroll difference is more than 5px to avoid jitter
      if (Math.abs(currentScrollY - lastScrollY.current) < 5) {
        return;
      }

      if (currentScrollY > lastScrollY.current) {
        // Scrolling down: hide category bar
        setIsCategoryBarVisible(false);
      } else {
        // Scrolling up: show category bar
        setIsCategoryBarVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Cart State Management
  const [cartItems, setCartItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem('rk_cart');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [isCustomerOrdersOpen, setIsCustomerOrdersOpen] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // Favorites/Wishlist State
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('rk_favorites');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [favoritesOnly, setFavoritesOnly] = useState(false);

  useEffect(() => {
    localStorage.setItem('rk_favorites', JSON.stringify(favorites));
  }, [favorites]);

  const toggleFavorite = useCallback((productId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setFavorites((prev) => {
      if (prev.includes(productId)) {
        return prev.filter((id) => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  }, []);

  const location = useLocation();
  const navigate = useNavigate();

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Automatically dismiss toast after 3 seconds
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Handle deep-linking to auto-open shared products on page load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const productId = params.get('product');
    if (productId && products.length > 0) {
      const prod = products.find((p) => p.id === productId);
      if (prod) {
        setSelectedProduct(prod);
        // Clear the product query parameter so it doesn't reopen if closed
        const newParams = new URLSearchParams(location.search);
        newParams.delete('product');
        const searchStr = newParams.toString();
        navigate(searchStr ? `/?${searchStr}` : '/', { replace: true });
      }
    }
  }, [location.search, products, navigate]);

  // Sync temporary quantity when opening or selecting a product
  useEffect(() => {
    if (selectedProduct) {
      const inCart = cartItems.find((item) => item.product.id === selectedProduct.id);
      setTempQuantity(inCart ? inCart.quantity : 1);
    }
  }, [selectedProduct, cartItems]);

  const copyToClipboardFallback = useCallback((text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        setToastMessage('Link copied! Opening WhatsApp... / लिंक कॉपी हो गया है! व्हाट्सएप खुल रहा है...');
        setTimeout(() => {
          const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
          window.open(whatsappUrl, '_blank');
        }, 1200);
      })
      .catch((err) => {
        console.error('[Share] Failed to copy text:', err);
        setToastMessage('Failed to share product. Please copy the link manually. / शेयर करने में त्रुटi हुई।');
      });
  }, []);

  const handleShareProduct = useCallback((product: Product, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const shareUrl = `${window.location.origin}/?product=${product.id}`;
    const title = `🛒 ${product.name} | Raj Kirana Store`;
    const text = `Buy ${product.name} (${product.quantityValue || ''} ${product.quantityUnit || ''}) for only ${formatPrice(product.price)} from Raj Kirana Store! Order now:\n`;
    const fullText = `${title}\n${text}${shareUrl}`;

    if (navigator.share) {
      navigator.share({
        title,
        text: `Buy ${product.name} from Raj Kirana Store!`,
        url: shareUrl,
      })
      .then(() => setToastMessage('Product shared successfully! / प्रोडक्ट शेयर हो गया!'))
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.debug('[Share] Error sharing via Web Share API:', err);
          copyToClipboardFallback(fullText);
        }
      });
    } else {
      copyToClipboardFallback(fullText);
    }
  }, [copyToClipboardFallback]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('filter') === 'favorites') {
      setFavoritesOnly(true);
      navigate('/', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    localStorage.setItem('rk_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = useCallback((product: Product, quantity: number = 1) => {
    setCartItems((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
        );
      }
      return [...prev, { product, quantity }];
    });
  }, []);

  const updateCartQuantity = useCallback((productId: string, quantity: number) => {
    setCartItems((prev) => {
      if (quantity <= 0) {
        return prev.filter((item) => item.product.id !== productId);
      }
      return prev.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      );
    });
  }, []);

  const clearCart = () => {
    setCartItems([]);
  };
  
  const [dataStatus, setDataStatus] = useState({ 
    products: false, 
    logo: true, // simplified
    configReceived: true, // simplified
    prodImages: true  // simplified
  });
  
  const observer = useRef<IntersectionObserver | null>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  const workerRef = useRef<Worker | null>(null);

  const isSearchEmpty = searchQuery.trim() === '';

  // Synchronize starting indexes with custom ticked image (selectedUrlIdx) if set in Admin panel
  useEffect(() => {
    if (config.banner1 && typeof config.banner1.selectedUrlIdx === 'number') {
      const urlsLen = config.banner1.urls?.length || 0;
      if (config.banner1.selectedUrlIdx >= 0 && config.banner1.selectedUrlIdx < (urlsLen || 1)) {
        setActiveBanner1Idx(config.banner1.selectedUrlIdx);
      }
    }
  }, [config.banner1?.selectedUrlIdx, config.banner1?.urls?.length]);

  useEffect(() => {
    if (config.banner2 && typeof config.banner2.selectedUrlIdx === 'number') {
      const urlsLen = config.banner2.urls?.length || 0;
      if (config.banner2.selectedUrlIdx >= 0 && config.banner2.selectedUrlIdx < (urlsLen || 1)) {
        setActiveBanner2Idx(config.banner2.selectedUrlIdx);
      }
    }
  }, [config.banner2?.selectedUrlIdx, config.banner2?.urls?.length]);

  // Auto-slide effect for Banner 1 and Banner 2
  useEffect(() => {
    const banner1 = config.banner1 || (config.bannerType && config.bannerType !== 'none' && config.bannerUrl ? { type: config.bannerType, url: config.bannerUrl, text: '' } : null);
    if (!banner1 || banner1.type === 'none') return;
    const urls = banner1.urls && banner1.urls.length > 0 
      ? banner1.urls.filter(Boolean) 
      : banner1.url ? [banner1.url] : [];
    if (urls.length <= 1) return;

    const isMarqueeEnabled = banner1.style 
      ? (banner1.style === 'marquee') 
      : (banner1.enableMarquee !== false);
    let selectedStyle = banner1.style || (isMarqueeEnabled ? 'marquee' : 'carousel');
    if (selectedStyle === 'spotlight') selectedStyle = 'carousel';
    
    if (selectedStyle !== 'carousel') return;

    const interval = setInterval(() => {
      setActiveBanner1Idx((prev) => (prev + 1) % urls.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [config.banner1, config.bannerType, config.bannerUrl]);

  useEffect(() => {
    const banner2 = config.banner2;
    if (!banner2 || banner2.type === 'none') return;
    const urls = banner2.urls && banner2.urls.length > 0 
      ? banner2.urls.filter(Boolean) 
      : banner2.url ? [banner2.url] : [];
    if (urls.length <= 1) return;

    const isMarqueeEnabled = banner2.style 
      ? (banner2.style === 'marquee') 
      : (banner2.enableMarquee === true);
    let selectedStyle = banner2.style || (isMarqueeEnabled ? 'marquee' : 'carousel');
    if (selectedStyle === 'spotlight') selectedStyle = 'carousel';

    if (selectedStyle !== 'carousel') return;

    const interval = setInterval(() => {
      setActiveBanner2Idx((prev) => (prev + 1) % urls.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [config.banner2]);

  useEffect(() => {
    // Dynamic SEO update
    document.title = "Raj Kirana Store";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'राज किराना स्टोर पर पाएं ताज़ा राशन, दाल, चावल, मसाले, कोल्ड ड्रिंक्स और दैनिक उपयोग का सामान सबसे किफायती दामों पर। Raj Kirana Store - Your trusted grocery shop.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Raj Kirana Store, Grocery Store, Kirana Shop, Rashan shop, Daily Essentials, General Store, Online Kirana, राज किराना स्टोर');
    }
  }, []);

  // Memoized products query - fetches all products once up to 2000 to enable instant hybrid in-memory search & sorting
  const productsQuery = useMemo(() => {
    return query(
      collection(db, 'products'),
      orderBy('createdAt', 'desc'),
      limit(2000)
    );
  }, []);

  // Initialize Web Worker for background processing and sorting
  useEffect(() => {
    const workerCode = `
      const CATEGORIES = [
        "Daily Essentials",
        "Groceries & Staples",
        "Personal Care",
        "Home Essentials",
        "Beverages",
        "Cosmetics",
        "Stationery"
      ];

      function getCollectionPriority(p) {
        if (p.isSpecial || p.isOffer) return 0;
        if (p.isNewArrival) return 1;
        if (p.isBestSeller) return 2;
        if (p.isTrending) return 3;
        return 4;
      }

      function getCategoryPriority(category) {
        const idx = CATEGORIES.indexOf(category);
        return idx === -1 ? 999 : idx;
      }

      self.onmessage = function(e) {
        const { products, action } = e.data;
        if (action === 'processProducts') {
          const sorted = [...products].sort((a, b) => {
            const orderA = a.order || 0;
            const orderB = b.order || 0;
            if (orderA > 0 && orderB > 0) {
              if (orderA !== orderB) return orderA - orderB;
            } else if (orderA > 0) {
              return -1;
            } else if (orderB > 0) {
              return 1;
            }
            
            const colPriA = getCollectionPriority(a);
            const colPriB = getCollectionPriority(b);
            if (colPriA !== colPriB) return colPriA - colPriB;
            
            const catPriA = getCategoryPriority(a.category);
            const catPriB = getCategoryPriority(b.category);
            if (catPriA !== catPriB) return catPriA - catPriB;
            
            const timeA = a.createdAt || 0;
            const timeB = b.createdAt || 0;
            return timeB - timeA;
          });
          self.postMessage({ sortedProducts: sorted });
        }
      };
    `;
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const workerUrl = URL.createObjectURL(blob);
    const worker = new Worker(workerUrl);
    workerRef.current = worker;

    return () => {
      worker.terminate();
      URL.revokeObjectURL(workerUrl);
    };
  }, []);

  useEffect(() => {
    let isCancelled = false;

    // 1. Listen for RootLayout critical collection preloading event for zero-delay hydration
    const handlePreloadedProducts = (e: Event) => {
      if (isCancelled) return;
      const customEvt = e as CustomEvent<Product[]>;
      if (customEvt.detail && Array.isArray(customEvt.detail)) {
        setMasterProductsList(customEvt.detail);
        setProducts(customEvt.detail);
        setDataStatus(prev => ({ ...prev, products: true }));
        setIsInitialLoad(false);
        setProductsError(null);
      }
    };
    window.addEventListener('rk_products_preloaded', handlePreloadedProducts);

    // 2. Immediate getDocs fallback if initial cache was cold and onSnapshot handshake is pending
    if (masterProductsList.length === 0) {
      getDocs(productsQuery).then((snap) => {
        if (isCancelled) return;
        const rawProds = snap.docs.map((d) => ({ id: d.id, ...d.data() } as Product));
        const sortedData = rawProds.sort((a, b) => {
          const orderA = a.order || 0;
          const orderB = b.order || 0;
          if (orderA > 0 && orderB > 0) {
            if (orderA !== orderB) return orderA - orderB;
          } else if (orderA > 0) {
            return -1;
          } else if (orderB > 0) {
            return 1;
          }
          const getCollectionPriority = (p: Product) => {
            if (p.isSpecial || p.isOffer) return 0;
            if (p.isNewArrival) return 1;
            if (p.isBestSeller) return 2;
            if (p.isTrending) return 3;
            return 4;
          };
          const colPriA = getCollectionPriority(a);
          const colPriB = getCollectionPriority(b);
          if (colPriA !== colPriB) return colPriA - colPriB;
          const catPriA = CATEGORIES.indexOf(a.category as any);
          const catPriB = CATEGORIES.indexOf(b.category as any);
          const cleanCatA = catPriA === -1 ? 999 : catPriA;
          const cleanCatB = catPriB === -1 ? 999 : catPriB;
          if (cleanCatA !== cleanCatB) return cleanCatA - cleanCatB;
          return (b.createdAt || 0) - (a.createdAt || 0);
        });
        setMasterProductsList(sortedData);
        setProducts(sortedData);
        setDataStatus(prev => ({ ...prev, products: true }));
        setIsInitialLoad(false);
        setProductsError(null);
      }).catch(err => {
        if (isCancelled) return;
        console.warn('Initial getDocs fallback fetch error:', err);
      });
    }

    // 3. Real-time Firestore updates via onSnapshot
    const unsubProducts = onSnapshot(
      productsQuery,
      (snapshot) => {
        if (isCancelled) return;
        const prodData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Product));
        setProductsError(null);
        
        if (workerRef.current) {
          workerRef.current.onmessage = (e) => {
            if (isCancelled) return;
            const { sortedProducts } = e.data;
            setMasterProductsList(sortedProducts);
            setProducts(sortedProducts);
            setDataStatus(prev => ({ ...prev, products: true }));
            setIsInitialLoad(false);
            try {
              localStorage.setItem('rk_products_cache', JSON.stringify(sortedProducts));
            } catch (err) {
              console.error('Failed to cache products:', err);
            }
          };
          workerRef.current.postMessage({ products: prodData, action: 'processProducts' });
        } else {
          // Fallback if worker is not yet ready
          const sortedData = prodData.sort((a, b) => {
            const orderA = a.order || 0;
            const orderB = b.order || 0;
            if (orderA > 0 && orderB > 0) {
              if (orderA !== orderB) return orderA - orderB;
            } else if (orderA > 0) {
              return -1;
            } else if (orderB > 0) {
              return 1;
            }
            const getCollectionPriority = (p: Product) => {
              if (p.isSpecial || p.isOffer) return 0;
              if (p.isNewArrival) return 1;
              if (p.isBestSeller) return 2;
              if (p.isTrending) return 3;
              return 4;
            };
            const colPriA = getCollectionPriority(a);
            const colPriB = getCollectionPriority(b);
            if (colPriA !== colPriB) return colPriA - colPriB;
            const catPriA = CATEGORIES.indexOf(a.category as any);
            const catPriB = CATEGORIES.indexOf(b.category as any);
            const cleanCatA = catPriA === -1 ? 999 : catPriA;
            const cleanCatB = catPriB === -1 ? 999 : catPriB;
            if (cleanCatA !== cleanCatB) return cleanCatA - cleanCatB;
            return (b.createdAt || 0) - (a.createdAt || 0);
          });
          setMasterProductsList(sortedData);
          setProducts(sortedData);
          setDataStatus(prev => ({ ...prev, products: true }));
          setIsInitialLoad(false);
          try {
            localStorage.setItem('rk_products_cache', JSON.stringify(sortedData));
          } catch (err) {
            console.error('Failed to cache products:', err);
          }
        }
      },
      (error) => {
        if (isCancelled) return;
        console.error('Firestore products onSnapshot error:', error);
        setProductsError('Unable to load products / उत्पाद लोड नहीं हो सके');
        setIsInitialLoad(false);
      }
    );

    return () => {
      isCancelled = true;
      window.removeEventListener('rk_products_preloaded', handlePreloadedProducts);
      unsubProducts();
    };
  }, [productsQuery, retryCount]);

  // SIGNAL READY WHEN ALL KEY ASSETS ARE FULLY LOADED
  useEffect(() => {
    if (dataStatus.products) {
      setIsInitialLoad(false);
    }
  }, [dataStatus.products]);

  // Initialize Fuse.js for superfast fuzzy searching
  const fuse = useMemo(() => {
    return new Fuse(masterProductsList, {
      keys: [
        { name: 'name', weight: 0.5 },
        { name: 'title', weight: 0.5 },
        { name: 'searchKeywords', weight: 0.3 },
        { name: 'tags', weight: 0.3 },
        { name: 'category', weight: 0.15 },
        { name: 'description', weight: 0.05 }
      ],
      threshold: 0.3, // Allows standard typos
      ignoreLocation: true,
      includeScore: true
    });
  }, [masterProductsList]);

  // Search, Collection & Category Logic (Memoized to prevent blocking main thread)
  const filteredProducts = useMemo(() => {
    const queryStr = searchQuery.trim();

    if (!queryStr) {
      // No search query active: Return normal filtered products list with related property cleared
      const filtered = masterProductsList.filter((p) => {
        let matchesCollection = true;
        if (selectedCollection === 'bestsellers') {
          matchesCollection = !!p.isBestSeller;
        } else if (selectedCollection === 'trending') {
          matchesCollection = !!p.isTrending;
        } else if (selectedCollection === 'newarrivals') {
          matchesCollection = !!p.isNewArrival;
        } else if (selectedCollection === 'offers') {
          matchesCollection = !!p.isOffer;
        }

        const matchesCategory = !selectedCategory || cleanCategoryName(p.category) === cleanCategoryName(selectedCategory);
        const matchesFavorites = !favoritesOnly || favorites.includes(p.id);
        return matchesCollection && matchesCategory && matchesFavorites;
      });
      return filtered.map(p => ({ ...p, isRelatedProduct: false }));
    }

    // Search query is active:
    // 1. Get direct matches using Fuse.js
    const searchResults = fuse.search(queryStr);
    const matchedItems = searchResults.map(res => res.item);

    // Apply other active filters to the direct matches
    const directMatches = matchedItems.filter((p) => {
      let matchesCollection = true;
      if (selectedCollection === 'bestsellers') {
        matchesCollection = !!p.isBestSeller;
      } else if (selectedCollection === 'trending') {
        matchesCollection = !!p.isTrending;
      } else if (selectedCollection === 'newarrivals') {
        matchesCollection = !!p.isNewArrival;
      } else if (selectedCollection === 'offers') {
        matchesCollection = !!p.isOffer;
      }

      const matchesCategory = !selectedCategory || cleanCategoryName(p.category) === cleanCategoryName(selectedCategory);
      const matchesFavorites = !favoritesOnly || favorites.includes(p.id);
      return matchesCollection && matchesCategory && matchesFavorites;
    }).map(p => ({ ...p, isRelatedProduct: false }));

    if (directMatches.length === 0) {
      // If there are absolutely no direct matches, return empty to trigger the "No products found" view
      return [];
    }

    // 2. Identify related products based on categories & tags of direct matches
    const directMatchIds = new Set(directMatches.map(p => p.id));
    const directMatchCategories = new Set(directMatches.map(p => cleanCategoryName(p.category)));
    const directMatchKeywords = new Set(directMatches.flatMap(p => p.searchKeywords || []));

    const relatedProducts = masterProductsList.filter((p) => {
      if (directMatchIds.has(p.id)) return false;

      // Must also pass active filters (collection, category, favorites)
      let matchesCollection = true;
      if (selectedCollection === 'bestsellers') {
        matchesCollection = !!p.isBestSeller;
      } else if (selectedCollection === 'trending') {
        matchesCollection = !!p.isTrending;
      } else if (selectedCollection === 'newarrivals') {
        matchesCollection = !!p.isNewArrival;
      } else if (selectedCollection === 'offers') {
        matchesCollection = !!p.isOffer;
      }

      const matchesCategory = !selectedCategory || cleanCategoryName(p.category) === cleanCategoryName(selectedCategory);
      const matchesFavorites = !favoritesOnly || favorites.includes(p.id);
      if (!(matchesCollection && matchesCategory && matchesFavorites)) return false;

      // Check if it belongs to same category or shares tags/keywords
      const isSameCategory = directMatchCategories.has(cleanCategoryName(p.category));
      const sharesKeywords = (p.searchKeywords || []).some(k => directMatchKeywords.has(k));

      return isSameCategory || sharesKeywords;
    }).map(p => ({ ...p, isRelatedProduct: true }));

    // 3. Return direct matches at the top, and related products below them!
    return [...directMatches, ...relatedProducts];
  }, [masterProductsList, fuse, searchQuery, selectedCategory, selectedCollection, favoritesOnly, favorites]);

  const specialItems = useMemo(() => {
    return masterProductsList.filter(p => p.isSpecial).slice(0, 2);
  }, [masterProductsList]);
  
  // Reset pagination index to initial 16 products whenever any filter/query changes
  useEffect(() => {
    setVisibleItems(16);
  }, [searchQuery, selectedCategory, selectedCollection, favoritesOnly]);

  const hasMore = filteredProducts.length > visibleItems;

  const lastElementRef = useCallback((node: HTMLDivElement | null) => {
    if (isInitialLoad) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        // Appends the next 16 products to the visible list
        setVisibleItems(prev => prev + 16);
      }
    }, { rootMargin: '300px' });

    if (node) observer.current.observe(node);
  }, [hasMore, isInitialLoad]);

  // Infinite scroll window scroll event fallback listener for 100% reliability
  useEffect(() => {
    if (isInitialLoad) return;
    
    const handleScroll = () => {
      if (!hasMore) return;
      
      const threshold = 350; // px from bottom of the viewport/document
      const totalHeight = document.documentElement.scrollHeight;
      const scrollPosition = window.innerHeight + window.scrollY;
      
      if (totalHeight - scrollPosition < threshold) {
        setVisibleItems((prev) => prev + 16);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, isInitialLoad]);

  const handleSelectCategoryFromBanner = useCallback((cat: string | null) => {
    setSelectedCategory(cat);
    setSelectedCollection(null);
    setFavoritesOnly(false);
    setTimeout(() => {
      const el = document.getElementById('categories-section');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }, []);

  const handleHomeRefresh = useCallback(async () => {
    try {
      // Re-fetch products snapshot from Firestore
      const snapshot = await getDocs(productsQuery);
      const prodData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Product));
      
      if (workerRef.current) {
        workerRef.current.postMessage({ products: prodData, action: 'processProducts' });
      } else {
        setMasterProductsList(prodData);
        setProducts(prodData);
      }
      
      // Update local storage cache
      localStorage.setItem('rk_products_cache', JSON.stringify(prodData));
      
      // Dispatch custom refresh event for other listeners
      window.dispatchEvent(new CustomEvent('app-home-refreshed'));
      
      setToastMessage('Live store synced! / स्टोर डेटा रीफ्रेश हो गया!');
    } catch (err) {
      console.warn('Home pull-to-refresh error:', err);
      setToastMessage('Refreshed / रीफ्रेश हो गया');
    }
  }, [productsQuery]);

  const displayProducts = useMemo(() => {
    return filteredProducts.slice(0, visibleItems);
  }, [filteredProducts, visibleItems]);

  return (
    <div className={`min-h-screen bg-[#FDFCF8] font-sans antialiased text-brand-text flex flex-col selection:bg-brand-accent selection:text-white pb-20 md:pb-0 ${selectedProduct ? 'overflow-hidden' : ''}`}>
      <Navbar 
        onSearch={setSearchQuery} 
        config={config} 
        onOpenOrders={() => setIsCustomerOrdersOpen(true)} 
        favoritesOnly={favoritesOnly}
        onToggleFavoritesOnly={() => setFavoritesOnly(prev => !prev)}
        favoritesCount={favorites.length}
        cartItemsCount={cartItems.length}
        cartTotal={cartItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0)}
        onOpenCart={() => setIsOrderModalOpen(true)}
        searchQuery={searchQuery}
      />

      {/* Premium Toast Notification System */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[200] bg-brand-accent text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 border border-emerald-950/20 backdrop-blur-md max-w-sm w-[calc(100vw-2rem)] md:w-auto text-xs md:text-sm font-semibold tracking-wide text-center"
          >
            <span>{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Banner Section (Top Banner) */}
      {renderBanner(
        config.banner1 || (config.bannerType && config.bannerType !== 'none' && config.bannerUrl ? { type: config.bannerType, url: config.bannerUrl, text: '' } : null),
        false,
        activeBanner1Idx,
        setActiveBanner1Idx
      )}

      {/* Categories Filter Section - Compact, Slim & Solid Style */}
      <section 
        id="categories-section" 
        className={`py-2 md:py-2.5 bg-[#FDFCF8]/95 backdrop-blur-md border-b border-gray-100/40 sticky transition-all duration-300 ease-in-out z-30 ${
          isCategoryBarVisible 
            ? 'top-32 lg:top-16 translate-y-0 opacity-100' 
            : 'top-32 lg:top-16 -translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-10 relative z-10">
          <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto md:overflow-x-visible pb-1 pt-0.5 scrollbar-hide no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap md:justify-center overflow-y-visible">
            {/* All Categories */}
            <button
              onClick={() => {
                setSelectedCategory(null);
                setSelectedCollection(null);
                setFavoritesOnly(false);
              }}
              className={`group flex flex-col md:flex-row items-center justify-center text-center gap-1.5 md:gap-2 px-3 py-2 md:px-4 md:py-1.5 rounded-2xl md:rounded-full border transition-all duration-300 flex-shrink-0 shadow-sm ${
                !selectedCategory && !selectedCollection && !favoritesOnly
                  ? 'bg-indigo-600 border-indigo-700 text-white shadow-md scale-[1.02]'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50/25'
              }`}
            >
              {showIcons && (
                <span>
                  {getCategoryIcon(null, !selectedCategory && !selectedCollection && !favoritesOnly)}
                </span>
              )}
              <span className="text-[10px] sm:text-xs md:text-[11px] font-extrabold tracking-wider uppercase whitespace-nowrap leading-none text-current">
                ALL ITEMS
              </span>
            </button>

            {/* 🏷️ Offers Collection Tab */}
            <button
              onClick={() => {
                setSelectedCollection('offers');
                setSelectedCategory(null);
                setFavoritesOnly(false);
              }}
              className={`group flex flex-col md:flex-row items-center justify-center text-center gap-1.5 md:gap-2 px-3 py-2 md:px-4 md:py-1.5 rounded-2xl md:rounded-full border transition-all duration-300 flex-shrink-0 shadow-sm ${
                selectedCollection === 'offers' && !favoritesOnly
                  ? 'bg-emerald-600 border-emerald-700 text-white shadow-md scale-[1.02]'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-emerald-300 hover:bg-emerald-50/25'
              }`}
            >
              {showIcons && (
                <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
                  selectedCollection === 'offers' && !favoritesOnly
                    ? 'bg-white/20 text-white'
                    : 'bg-emerald-50 text-emerald-500 group-hover:bg-emerald-100'
                }`}>
                  <Tag className={`w-5 h-5 md:w-4 h-4 transition-transform duration-300 group-hover:scale-110 ${selectedCollection === 'offers' && !favoritesOnly ? 'text-white' : 'text-emerald-500'}`} />
                </div>
              )}
              <span className="text-[10px] sm:text-xs md:text-[11px] font-extrabold tracking-wider uppercase whitespace-nowrap leading-none text-current">
                SPECIAL OFFERS
              </span>
            </button>

            {/* ✨ New Arrivals Collection Tab */}
            <button
              onClick={() => {
                setSelectedCollection('newarrivals');
                setSelectedCategory(null);
                setFavoritesOnly(false);
              }}
              className={`group flex flex-col md:flex-row items-center justify-center text-center gap-1.5 md:gap-2 px-3 py-2 md:px-4 md:py-1.5 rounded-2xl md:rounded-full border transition-all duration-300 flex-shrink-0 shadow-sm ${
                selectedCollection === 'newarrivals' && !favoritesOnly
                  ? 'bg-blue-600 border-blue-700 text-white shadow-md scale-[1.02]'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-blue-300 hover:bg-blue-50/25'
              }`}
            >
              {showIcons && (
                <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
                  selectedCollection === 'newarrivals' && !favoritesOnly
                    ? 'bg-white/20 text-white'
                    : 'bg-blue-50 text-blue-500 group-hover:bg-blue-100'
                }`}>
                  <Sparkles className={`w-5 h-5 md:w-4 h-4 transition-transform duration-300 group-hover:scale-110 ${selectedCollection === 'newarrivals' && !favoritesOnly ? 'text-white' : 'text-blue-500'}`} />
                </div>
              )}
              <span className="text-[10px] sm:text-xs md:text-[11px] font-extrabold tracking-wider uppercase whitespace-nowrap leading-none text-current">
                NEW ARRIVALS
              </span>
            </button>

            {/* 🔥 Best Sellers Collection Tab */}
            <button
              onClick={() => {
                setSelectedCollection('bestsellers');
                setSelectedCategory(null);
                setFavoritesOnly(false);
              }}
              className={`group flex flex-col md:flex-row items-center justify-center text-center gap-1.5 md:gap-2 px-3 py-2 md:px-4 md:py-1.5 rounded-2xl md:rounded-full border transition-all duration-300 flex-shrink-0 shadow-sm ${
                selectedCollection === 'bestsellers' && !favoritesOnly
                  ? 'bg-amber-500 border-amber-600 text-white shadow-md scale-[1.02]'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-amber-300 hover:bg-amber-50/25'
              }`}
            >
              {showIcons && (
                <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
                  selectedCollection === 'bestsellers' && !favoritesOnly
                    ? 'bg-white/20 text-white'
                    : 'bg-amber-50 text-amber-500 group-hover:bg-amber-100'
                }`}>
                  <Flame className={`w-5 h-5 md:w-4 h-4 transition-transform duration-300 group-hover:scale-110 ${selectedCollection === 'bestsellers' && !favoritesOnly ? 'fill-white text-white' : 'fill-amber-500 text-amber-500'}`} />
                </div>
              )}
              <span className="text-[10px] sm:text-xs md:text-[11px] font-extrabold tracking-wider uppercase whitespace-nowrap leading-none text-current">
                BEST SELLERS
              </span>
            </button>

            {/* ⚡ Trending Collection Tab */}
            <button
              onClick={() => {
                setSelectedCollection('trending');
                setSelectedCategory(null);
                setFavoritesOnly(false);
              }}
              className={`group flex flex-col md:flex-row items-center justify-center text-center gap-1.5 md:gap-2 px-3 py-2 md:px-4 md:py-1.5 rounded-2xl md:rounded-full border transition-all duration-300 flex-shrink-0 shadow-sm ${
                selectedCollection === 'trending' && !favoritesOnly
                  ? 'bg-violet-600 border-violet-700 text-white shadow-md scale-[1.02]'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-violet-300 hover:bg-violet-50/25'
              }`}
            >
              {showIcons && (
                <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
                  selectedCollection === 'trending' && !favoritesOnly
                    ? 'bg-white/20 text-white'
                    : 'bg-violet-50 text-violet-500 group-hover:bg-violet-100'
                }`}>
                  <Zap className={`w-5 h-5 md:w-4 h-4 transition-transform duration-300 group-hover:scale-110 ${selectedCollection === 'trending' && !favoritesOnly ? 'fill-white text-white' : 'fill-violet-500 text-violet-500'}`} />
                </div>
              )}
              <span className="text-[10px] sm:text-xs md:text-[11px] font-extrabold tracking-wider uppercase whitespace-nowrap leading-none text-current">
                TRENDING
              </span>
            </button>

            {/* My Favorites Virtual Category Tab */}
            <button
              onClick={() => {
                setFavoritesOnly(true);
                setSelectedCategory(null);
                setSelectedCollection(null);
              }}
              className={`group flex flex-col md:flex-row items-center justify-center text-center gap-1.5 md:gap-2 px-3 py-2 md:px-4 md:py-1.5 rounded-2xl md:rounded-full border transition-all duration-300 flex-shrink-0 shadow-sm ${
                favoritesOnly && !selectedCategory && !selectedCollection
                  ? 'bg-rose-600 border-rose-700 text-white shadow-md scale-[1.02]'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-rose-300 hover:bg-rose-50/25'
              }`}
            >
              {showIcons && (
                <div className={`p-1.5 rounded-lg flex items-center justify-center transition-all ${
                  favoritesOnly && !selectedCategory && !selectedCollection
                    ? 'bg-white/20 text-white'
                    : 'bg-rose-50 text-rose-500 group-hover:bg-rose-100'
                }`}>
                  <Heart className={`w-5 h-5 md:w-4 h-4 transition-transform duration-300 group-hover:scale-110 ${favoritesOnly && !selectedCategory && !selectedCollection ? 'fill-white text-white' : 'fill-rose-500 text-rose-500'}`} />
                </div>
              )}
              <span className="text-[10px] sm:text-xs md:text-[11px] font-extrabold tracking-wider uppercase whitespace-nowrap leading-none text-current">
                FAVORITES ({favorites.length})
              </span>
            </button>

            {CATEGORIES.map((cat) => {
              const isSelected = selectedCategory === cat && !favoritesOnly && !selectedCollection;
              
              // Get category-specific colors for active/hover states
              let activeBg = 'bg-brand-accent border-brand-accent';
              let hoverBorder = 'hover:border-brand-accent/40 hover:bg-brand-accent/5';
              const catLower = cat.toLowerCase();
              if (catLower.includes("daily") || catLower.includes("rozana")) {
                activeBg = 'bg-amber-500 border-amber-600';
                hoverBorder = 'hover:border-amber-300 hover:bg-amber-50/25';
              } else if (catLower.includes("grocer") || catLower.includes("rashan")) {
                activeBg = 'bg-emerald-600 border-emerald-700';
                hoverBorder = 'hover:border-emerald-300 hover:bg-emerald-50/25';
              } else if (catLower.includes("personal") || catLower.includes("dekhbhal")) {
                activeBg = 'bg-teal-600 border-teal-700';
                hoverBorder = 'hover:border-teal-300 hover:bg-teal-50/25';
              } else if (catLower.includes("home") || catLower.includes("ghar")) {
                activeBg = 'bg-blue-600 border-blue-700';
                hoverBorder = 'hover:border-blue-300 hover:bg-blue-50/25';
              } else if (catLower.includes("beverage") || catLower.includes("peene")) {
                activeBg = 'bg-orange-600 border-orange-700';
                hoverBorder = 'hover:border-orange-300 hover:bg-orange-50/25';
              } else if (catLower.includes("cosmetic") || catLower.includes("shringar")) {
                activeBg = 'bg-fuchsia-600 border-fuchsia-700';
                hoverBorder = 'hover:border-fuchsia-300 hover:bg-fuchsia-50/25';
              } else if (catLower.includes("stationery") || catLower.includes("lekhan")) {
                activeBg = 'bg-violet-600 border-violet-700';
                hoverBorder = 'hover:border-violet-300 hover:bg-violet-50/25';
              }

              return (
                <button
                  key={cat}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setSelectedCollection(null);
                    setFavoritesOnly(false);
                  }}
                  className={`group flex flex-col md:flex-row items-center justify-center text-center gap-1.5 md:gap-2 px-3 py-2 md:px-4 md:py-1.5 rounded-2xl md:rounded-full border transition-all duration-300 flex-shrink-0 shadow-sm ${
                    isSelected
                      ? `${activeBg} text-white shadow-md scale-[1.02]`
                      : `bg-white border-gray-200 text-gray-700 ${hoverBorder}`
                  }`}
                >
                  {showIcons && (
                    <span>
                      {getCategoryIcon(cat, isSelected)}
                    </span>
                  )}
                  <span className="text-[10px] sm:text-xs md:text-[11px] font-extrabold tracking-wider uppercase whitespace-nowrap leading-none text-current">
                    {cleanCategoryName(cat).toUpperCase()}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Banner Section 2 (Below Category Bar) */}
      {renderBanner(config.banner2, true, activeBanner2Idx, setActiveBanner2Idx)}

      {/* Main Product Feed */}



      <main className="flex-1 px-4 md:px-10 py-2 sm:py-3 md:py-4 max-w-7xl mx-auto w-full">
        {favoritesOnly && (
          <div className="mb-4 border-l-4 border-red-500 pl-4 py-2.5 bg-red-500/5 flex items-center justify-between rounded-r-xl">
            <div>
              <h2 className="text-xl font-display font-bold text-red-600 flex items-center gap-2">
                <Heart className="w-5 h-5 fill-red-500 text-red-500" />
                <span>My Favorites / मेरे पसंदीदा</span>
              </h2>
              <p className="text-xs text-brand-muted mt-0.5">
                {filteredProducts.length} {filteredProducts.length === 1 ? 'item' : 'items'} saved in your wishlist
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFavoritesOnly(false)}
              className="text-xs font-bold text-brand-accent hover:text-brand-dark px-3 py-1.5 border border-brand-accent/20 hover:border-brand-accent/40 rounded-lg bg-white transition-all cursor-pointer shadow-sm"
            >
              Show All Products
            </button>
          </div>
        )}

        {selectedCollection && !favoritesOnly && (
          <div className="mb-4 border-l-4 border-amber-500 pl-4 py-2.5 bg-amber-500/5 flex items-center justify-between rounded-r-xl">
            <div>
              <h2 className="text-xl font-display font-bold text-gray-900 flex items-center gap-2">
                {selectedCollection === 'bestsellers' && <Flame className="w-5 h-5 text-amber-500 fill-amber-500" />}
                {selectedCollection === 'trending' && <Zap className="w-5 h-5 text-violet-500 fill-violet-500" />}
                {selectedCollection === 'newarrivals' && <Sparkles className="w-5 h-5 text-blue-500" />}
                {selectedCollection === 'offers' && <Tag className="w-5 h-5 text-emerald-500" />}
                <span>
                  {selectedCollection === 'bestsellers' && '🔥 Best Sellers Collection'}
                  {selectedCollection === 'trending' && '⚡ Trending Items Collection'}
                  {selectedCollection === 'newarrivals' && '✨ New Arrivals Collection'}
                  {selectedCollection === 'offers' && '🏷️ Special Offers & Discounts'}
                </span>
              </h2>
              <p className="text-xs text-brand-muted mt-0.5">
                {filteredProducts.length} {filteredProducts.length === 1 ? 'item' : 'items'} available in this collection
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedCollection(null)}
              className="text-xs font-bold text-brand-accent hover:text-brand-dark px-3 py-1.5 border border-brand-accent/20 hover:border-brand-accent/40 rounded-lg bg-white transition-all cursor-pointer shadow-xs"
            >
              Show All Products
            </button>
          </div>
        )}

        {selectedCategory && !favoritesOnly && !selectedCollection && (
          <div className="mb-4 border-l-4 border-brand-accent pl-4 py-2.5 bg-brand-accent/5 flex items-center justify-between rounded-r-xl">
            <div>
              <h2 className="text-xl font-display font-bold text-brand-accent">{cleanCategoryName(selectedCategory)}</h2>
              <p className="text-xs text-brand-muted mt-0.5">{filteredProducts.length} items available in this section</p>
            </div>
            <button
              type="button"
              onClick={() => setSelectedCategory(null)}
              className="text-xs font-bold text-brand-accent hover:text-brand-dark px-3 py-1.5 border border-brand-accent/20 hover:border-brand-accent/40 rounded-lg bg-white transition-all cursor-pointer shadow-xs"
            >
              Show All Products
            </button>
          </div>
        )}
        
        <AnimatePresence mode="wait">
        {isInitialLoad ? (
          <motion.div 
            key="skeleton-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {config.productLayout === 'carousel' ? (
              <div className="flex items-stretch gap-4 md:gap-6 overflow-x-auto pb-4 no-scrollbar">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="w-[185px] xs:w-[210px] sm:w-[240px] md:w-[280px] shrink-0">
                    <ProductCardSkeleton />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-2.5 md:gap-3">
                {[...Array(8)].map((_, i) => (
                  <ProductCardSkeleton key={i} />
                ))}
              </div>
            )}
          </motion.div>
        ) : productsError ? (
          <motion.div 
            key="error-view"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col items-center justify-center max-w-md mx-auto p-8 rounded-2xl bg-red-50/50 border border-red-200/60 text-center shadow-xs my-8"
          >
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600 stroke-[1.75]" />
            </div>
            
            <h3 className="font-display text-xl font-bold text-gray-900 tracking-tight">
              Unable to load products / उत्पाद लोड नहीं हो सके
            </h3>
            <p className="mt-2 text-xs text-gray-600 leading-relaxed max-w-xs">
              {productsError}
            </p>
            
            <div className="mt-6 flex flex-col gap-2 w-full">
              <button
                type="button"
                onClick={handleRetry}
                className="w-full px-5 py-2.5 bg-brand-accent hover:bg-brand-dark text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all cursor-pointer active:scale-98 flex items-center justify-center gap-2"
              >
                <RotateCw className="w-4 h-4" />
                <span>Retry / पुनः प्रयास करें</span>
              </button>
            </div>
          </motion.div>
        ) : displayProducts.length > 0 ? (
          <motion.div
            key="products-view"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <ProductFeedLayouts 
              layout={config.productLayout || 'standard_grid'}
              products={displayProducts}
              inlineAds={config.inlineAds}
              onProductClick={setSelectedProduct}
              favorites={favorites}
              onToggleFavorite={toggleFavorite}
              onAddToCart={addToCart}
              cartItems={cartItems}
              onUpdateCartQuantity={updateCartQuantity}
              onSelectCategory={handleSelectCategoryFromBanner}
              onSelectCollection={setSelectedCollection}
              masterProductsList={products}
              onShare={handleShareProduct}
              searchQuery={searchQuery}
            />
          </motion.div>
        ) : (
          <div className="text-center py-20 bg-gray-50 editorial-card mx-2 flex flex-col items-center justify-center">
            {favoritesOnly ? (
              <>
                <Heart className="w-12 h-12 text-red-300 mb-4 animate-pulse fill-red-100" />
                <h3 className="font-display text-2xl font-bold text-brand-text">Your Wishlist is Empty</h3>
                <p className="mt-2 text-sm text-gray-400 max-w-sm">
                  Tap the heart icon on any product card to save your favorite groceries here!
                </p>
                <button
                  onClick={() => setFavoritesOnly(false)}
                  className="mt-6 px-4 py-2 bg-brand-accent text-white rounded-xl text-xs font-bold shadow-md hover:bg-opacity-95 transition-all cursor-pointer"
                >
                  Browse Products
                </button>
              </>
            ) : masterProductsList.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center max-w-md mx-auto p-8 rounded-2xl bg-[#FDFCF8] border border-brand-border/40 text-center shadow-xs my-8"
              >
                <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-4">
                  <PackageX className="w-8 h-8 text-amber-600 stroke-[1.5]" />
                </div>
                
                <h3 className="font-display text-xl font-bold text-gray-900 tracking-tight">
                  No products found / कोई उत्पाद नहीं मिला
                </h3>
                <p className="mt-2 text-xs text-brand-muted leading-relaxed max-w-xs">
                  There are currently no products available in the store. Please check back later.
                </p>
              </motion.div>
            ) : (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center max-w-md mx-auto p-8 rounded-2xl bg-[#FDFCF8] border border-brand-border/40 text-center shadow-xs my-8"
              >
                <div className="relative mb-6">
                  {/* Elegant decorative background ring */}
                  <div className="w-20 h-20 bg-[#2A5431]/5 rounded-full flex items-center justify-center animate-pulse" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Search className="w-10 h-10 text-brand-muted stroke-[1.5]" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 bg-amber-100 p-1.5 rounded-full border border-amber-200">
                    <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                  </div>
                </div>
                
                <h3 className="font-display text-xl font-bold text-gray-900 tracking-tight">
                  No products found / कोई उत्पाद नहीं मिला
                </h3>
                <p className="mt-2 text-xs text-brand-muted leading-relaxed max-w-xs">
                  We couldn't find any products matching{' '}
                  {searchQuery ? <strong className="text-brand-accent">"{searchQuery}"</strong> : ''}
                  {selectedCategory ? ` in ${cleanCategoryName(selectedCategory)}` : ''}
                  {selectedCollection ? ` in ${selectedCollection}` : ''}.
                </p>
                
                <div className="mt-6 flex flex-col gap-2 w-full">
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory(null);
                      setSelectedCollection(null);
                      setFavoritesOnly(false);
                    }}
                    className="w-full px-4 py-2.5 bg-brand-accent hover:bg-brand-dark text-white rounded-xl text-xs font-bold shadow-md hover:shadow-lg transition-all cursor-pointer active:scale-98"
                  >
                    Clear All Filters / फ़िल्टर हटाएं
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        )}
        </AnimatePresence>

        {hasMore && (
          <div ref={lastElementRef} className="mt-12 py-10 flex flex-col items-center justify-center gap-4">
            <div className="w-8 h-8 border-2 border-brand-accent/20 border-t-brand-accent rounded-full animate-spin" />
            <p className="text-[10px] uppercase font-bold tracking-widest text-brand-muted animate-pulse">
              Fetching more products...
            </p>
          </div>
        )}
      </main>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
          style={{
            paddingTop: 'max(0.75rem, env(safe-area-inset-top))',
            paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))',
            paddingLeft: 'max(0.75rem, env(safe-area-inset-left))',
            paddingRight: 'max(0.75rem, env(safe-area-inset-right))',
          }}
        >
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setSelectedProduct(null);
              setTempQuantity(1);
            }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md"
          />
          <motion.div 
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative bg-white w-full max-w-3xl rounded-2xl sm:rounded-3xl border border-brand-border/80 overflow-hidden shadow-2xl flex flex-col md:flex-row my-auto max-h-[calc(100dvh-1.5rem)] sm:max-h-[85vh]"
          >
            {/* Close Button */}
            <button 
              onClick={() => {
                setSelectedProduct(null);
                setTempQuantity(1);
              }}
              className="absolute top-3 right-3 z-20 w-9 h-9 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-md hover:bg-white transition-all hover:scale-105 active:scale-95 text-gray-700 hover:text-black border border-gray-100 cursor-pointer"
              title="Close / बंद करें"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Share Button */}
            <button 
              onClick={(e) => handleShareProduct(selectedProduct, e)}
              className="absolute top-3 left-3 z-20 w-9 h-9 bg-white/90 backdrop-blur-md rounded-full flex items-center justify-center shadow-md hover:bg-white transition-all hover:scale-105 active:scale-95 text-brand-accent border border-gray-100 cursor-pointer"
              title="Share / साझा करें"
            >
              <Share2 className="w-4 h-4" />
            </button>

            {/* Product Image Section */}
            <div className="relative w-full md:w-[360px] lg:w-[420px] aspect-square flex-shrink-0 flex items-center justify-center bg-gradient-to-br from-gray-50 via-emerald-50/20 to-gray-50 border-b md:border-b-0 md:border-r border-brand-border/60 overflow-hidden group">
              {selectedProduct.imageUrl ? (
                <div 
                  onClick={() => setIsLightboxOpen(true)}
                  className="relative w-full h-full aspect-square flex items-center justify-center p-0 cursor-zoom-in"
                  title="Click to zoom image / बड़ा देखने के लिए क्लिक करें"
                >
                  <img 
                    src={selectedProduct.imageUrl} 
                    alt={selectedProduct.name} 
                    width={600}
                    height={600}
                    loading="lazy"
                    className="w-full h-full max-h-full max-w-full object-contain drop-shadow-xs transition-transform duration-500 group-hover:scale-110 select-none"
                    referrerPolicy="no-referrer"
                  />
                  {/* Zoom Badge */}
                  <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-md px-2.5 py-1 rounded-full text-[10px] font-extrabold text-gray-700 border border-brand-border shadow-xs flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <ZoomIn className="w-3 h-3 text-brand-accent" />
                    <span>Zoom</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 opacity-15">
                  <Star className="w-16 h-16 text-brand-accent" />
                  <span className="font-display font-black text-3xl italic tracking-tighter">RK</span>
                </div>
              )}

              {/* Stock Status Badge */}
              <div className="absolute top-3 left-14 z-10">
                {selectedProduct.available ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-emerald-800 bg-emerald-100/90 backdrop-blur-sm px-2.5 py-1 rounded-full border border-emerald-200 shadow-xs">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 animate-pulse" />
                    In Stock
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-red-800 bg-red-100/90 backdrop-blur-sm px-2.5 py-1 rounded-full border border-red-200 shadow-xs">
                    Out of Stock
                  </span>
                )}
              </div>
            </div>

            {/* Product Info Section (Restructured with Scrollable Body & Pinned Footer) */}
            <div className="flex-1 min-w-0 min-h-0 flex flex-col bg-white overflow-hidden">
              {/* Smooth Scrollable Body */}
              <div className="p-5 md:p-6 flex-1 overflow-y-auto space-y-4">
                {/* Category & Net Qty row */}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-[10px] font-black uppercase tracking-wider text-brand-accent bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
                    {cleanCategoryName(selectedProduct.category)}
                  </span>
                  {selectedProduct.showQuantity && selectedProduct.quantityValue && (
                    <span className="text-xs font-bold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                      Net Qty: {selectedProduct.quantityValue} {formatQuantityUnit(selectedProduct.quantityUnit || 'g')}
                    </span>
                  )}
                </div>

                {/* Title */}
                <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-black text-brand-text leading-snug">
                  {selectedProduct.name}
                </h2>

                {/* Price & Discount Row */}
                <div className="flex items-baseline gap-3 pb-2 border-b border-brand-border/60">
                  <span className="text-2xl sm:text-3xl font-display font-black text-brand-accent">
                    {formatPrice(selectedProduct.price)}
                  </span>
                  {selectedProduct.mrp && selectedProduct.mrp > selectedProduct.price && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-brand-muted line-through font-semibold">
                        {formatPrice(selectedProduct.mrp)}
                      </span>
                      <span className="text-[11px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-black uppercase border border-emerald-200">
                        SAVE {formatPrice(selectedProduct.mrp - selectedProduct.price)}
                      </span>
                    </div>
                  )}
                </div>

                {/* QUANTITY SELECTOR (IN SCROLLABLE BODY ABOVE DESCRIPTION) */}
                {selectedProduct.available && (
                  <div className="bg-gray-50/80 p-2 sm:p-2.5 rounded-2xl border border-brand-border/60">
                    <QuantitySelector 
                      size="md"
                      label="Select Quantity / मात्रा चुनें"
                      showPresets={true}
                      min={0}
                      initialQuantity={cartItems.find((item) => item.product.id === selectedProduct.id)?.quantity ?? tempQuantity}
                      onQuantityChange={(qty) => {
                        const isInCart = Boolean(cartItems.find((item) => item.product.id === selectedProduct.id));
                        if (qty <= 0) {
                          if (isInCart) {
                            updateCartQuantity(selectedProduct.id, 0);
                          }
                          setTempQuantity(0);
                        } else {
                          if (isInCart) {
                            updateCartQuantity(selectedProduct.id, qty);
                          }
                          setTempQuantity(qty);
                        }
                      }}
                    />
                  </div>
                )}

                {/* PRODUCT DESCRIPTION SECTION */}
                <div className="pt-1 space-y-1.5 pb-2">
                  <h4 className="text-xs font-black uppercase tracking-wider text-brand-muted flex items-center gap-1.5">
                    <span>Product Description / विवरण</span>
                  </h4>
                  <div className="bg-gray-50/80 border border-brand-border/60 rounded-2xl p-3.5">
                    <p className="text-xs sm:text-sm text-gray-600 leading-relaxed font-medium">
                      {selectedProduct.description || "No description available / कोई विवरण उपलब्ध नहीं है"}
                    </p>
                  </div>
                </div>
              </div>

              {/* PINNED ACTION FOOTER (ALWAYS VISIBLE AT BOTTOM) */}
              <div className="p-4 sm:p-5 bg-white/95 backdrop-blur-md border-t border-brand-border/60 shrink-0 shadow-lg z-10">
                {selectedProduct.available ? (
                  (() => {
                    const existingCartItem = cartItems.find((item) => item.product.id === selectedProduct.id);
                    if (existingCartItem) {
                      return (
                        <div className="flex items-center justify-between bg-emerald-100/80 border border-emerald-200 p-3 sm:p-3.5 rounded-2xl shadow-xs">
                          <div className="flex flex-col">
                            <span className="text-xs font-black text-emerald-900 flex items-center gap-1.5">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                              Added to Bag ({existingCartItem.quantity} {existingCartItem.quantity === 1 ? 'unit' : 'units'})
                            </span>
                            <div className="flex items-center gap-1.5 pl-5 mt-0.5">
                              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-800">Total:</span>
                              <span className="text-xs font-black text-emerald-950 bg-white/90 border border-emerald-300/80 px-2 py-0.5 rounded-md shadow-2xs">
                                {formatPrice(selectedProduct.price * existingCartItem.quantity)}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setSelectedProduct(null);
                              setIsOrderModalOpen(true);
                            }}
                            className="text-xs font-black text-white bg-emerald-700 hover:bg-emerald-800 px-3.5 py-2 rounded-xl transition-colors shadow-xs cursor-pointer shrink-0 ml-2"
                          >
                            View Cart →
                          </button>
                        </div>
                      );
                    }
                    const buttonQty = tempQuantity > 0 ? tempQuantity : 1;
                    return (
                      <button
                        onClick={() => {
                          addToCart(selectedProduct, buttonQty);
                          setTempQuantity(buttonQty);
                        }}
                        className="w-full bg-brand-accent text-white p-3.5 sm:p-4 rounded-2xl font-black text-sm shadow-md hover:bg-emerald-700 active:scale-[0.98] transition-all flex items-center justify-center gap-2 cursor-pointer"
                      >
                        <ShoppingBag className="w-5 h-5" /> 
                        <span>Add {buttonQty} to Cart • {formatPrice(selectedProduct.price * buttonQty)}</span>
                      </button>
                    );
                  })()
                ) : (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs font-black uppercase tracking-wider rounded-2xl text-center shadow-xs">
                    Currently Out of Stock / स्टॉक में नहीं है
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Order & Checkout Modal */}
      <Suspense fallback={null}>
        <OrderModal
          isOpen={isOrderModalOpen}
          onClose={() => setIsOrderModalOpen(false)}
          cartItems={cartItems}
          onUpdateQuantity={updateCartQuantity}
          onClearCart={clearCart}
        />
      </Suspense>

      {/* Customer Tracking Drawer */}
      <Suspense fallback={null}>
        <CustomerOrdersModal
          isOpen={isCustomerOrdersOpen}
          onClose={() => setIsCustomerOrdersOpen(false)}
        />
      </Suspense>

      {/* Product Image Lightbox Overlay */}
      {selectedProduct && selectedProduct.imageUrl && (
        <ProductLightbox
          isOpen={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
          imageUrl={selectedProduct.imageUrl}
          productName={selectedProduct.name}
        />
      )}

      <footer className="border-t border-brand-border py-10 px-6 text-center text-xs text-brand-muted uppercase tracking-[0.2em]">
        &copy; {new Date().getFullYear()} Raj Kirana Store &bull; Quality and Freshness
      </footer>
    </div>
  );
}
